import { DialogActions, type Theme, useTheme } from "@mui/material";
import { type FC, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import type { ExperienceSettingsFormValues } from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsModal";
import * as S from "@/VisualBuilder/components/ExperienceSettingsDialog/styled";
import { PROPERTY_TIERS, ROLES } from "@/constants";
import { selectUser } from "@/store/auth/auth.slice";
import { useThunkSelector } from "@/store/hooks";
import { currentExperienceSelector } from "@/store/v2/currentExperience/selectors";
import { currentPropertySelector } from "@/store/v2/currentProperty/selectors";

interface ExperienceSettingsActionsProps {
  readonly onSubmit: () => Promise<void>;
  readonly handleDuplicateExperience: () => void;
  readonly handleConvertToTemplate: () => void;
}

const ExperienceSettingsActions: FC<ExperienceSettingsActionsProps> = ({
  onSubmit,
  handleDuplicateExperience,
  handleConvertToTemplate,
}) => {
  const theme = useTheme<Theme>();

  const { experience } = useSelector(currentExperienceSelector);
  const { property } = useSelector(currentPropertySelector);
  const user = useThunkSelector(selectUser);
  const location = useLocation();

  const isSalesperson = !!user && user.userRole.title === ROLES.SALESPERSON;

  const { isTemplate } = experience;
  const isCollection = experience.userCollection === true;

  const isSalesHubTier = property.tier?.title === PROPERTY_TIERS.STANDARD;

  const { formState, handleSubmit } = useFormContext<ExperienceSettingsFormValues>();

  const isOrganizationsSection = location.pathname.startsWith("/organizations");

  const isSaveDisabled =
    formState.isSubmitting ||
    formState.isValidating ||
    !formState.isValid ||
    !Object.keys(formState.dirtyFields).length;

  const onSaveClick = useCallback(() => {
    void handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <DialogActions>
      {(!isSalesHubTier || isOrganizationsSection) && (
        <S.DialogButton
          data-testid="duplicate-button"
          className="firstButton"
          $theme={theme}
          onClick={handleDuplicateExperience}
        >
          Duplicate
        </S.DialogButton>
      )}
      {!isTemplate && !isCollection && !isSalesperson && (
        <S.DialogButton data-testid="convert-button" $theme={theme} onClick={handleConvertToTemplate}>
          Convert to Template
        </S.DialogButton>
      )}
      <S.DialogButton
        disabled={isSaveDisabled}
        data-testid="save-button"
        className="submitButton"
        $theme={theme}
        onClick={onSaveClick}
      >
        Save
      </S.DialogButton>
    </DialogActions>
  );
};

export default ExperienceSettingsActions;
